<script lang="ts">
export interface WSelectProps {
  initialOptions?: (
    initialValues?: any,
    where?: Record<string, any>,
  ) =>
    | { value: string | number; label: string }[]
    | Promise<{ value: string | number; label: string }[]>
  options?: (
    initialValues?: any,
    where?: any,
  ) =>
    | { value: string | number; label: string }[]
    | Promise<{ value: string | number; label: string }[]>
  where?: Record<string, any>
  // where?: (state?: any) => any
  mode?: 'tags' | 'tags-long' | 'multiple' | 'single'
  placeholder?: string
  view?: string
  delay?: number
  size?: string
  keepValuesOnWatch?: boolean
  filterResults?: boolean
  resolveOnLoad?: boolean
  canDeselect?: boolean
  searchable?: boolean
  showAddAll?: boolean
  canClear?: boolean
  disabled?: boolean
  object?: boolean
  attrs?: any
  caret?: boolean
  // here used for styling
  formkit?: boolean
}
</script>

<script setup lang="ts">
import Multiselect from '@vueform/multiselect'

const props = withDefaults(defineProps<WSelectProps>(), {
  // where: null, // () => null,
  mode: 'single',
  placeholder: '',
  delay: 100,
  size: null,
  keepValuesOnWatch: false,
  filterResults: true,
  resolveOnLoad: true,
  canDeselect: false,
  searchable: true,
  showAddAll: false,
  canClear: true,
  formkit: false,
  object: false,
  caret: true,
  where: null,
})

const emits = defineEmits(['change:modelValue'])
const model = defineModel()

type Item = { value: number; label: string }

const selectRef = ref(null)
const loading = ref(false)
const forceInitialOptionsLoad = ref(false)
const loadedOptions = ref(false)

function onInput(value: any) {
  if (
    !selectRef.value ||
    !selectRef.value.input ||
    areEqual(value, model.value)
  )
    return
  selectRef.value.search = ''
  selectRef.value.blur()
}

const mode = computed<Multiselect['$props']['mode']>(() => {
  if (props.mode === 'tags-long') return 'tags'
  return props.mode
})

const emptyValue = computed(() => (mode.value === 'single' ? null : []))
const isSingle = computed(
  () => !(props.mode === 'tags' || props.mode === 'multiple'),
)
function flushValue() {
  console.log('[Select] Flushing value: ', model.value)
  // clearSelectOptions()
  model.value = emptyValue.value
}

// Easy access to multiselect internals
const msHasSelectedValue = computed(() => {
  const msSelectedValue = selectRef.value?.iv
  return (
    msSelectedValue &&
    (Array.isArray(msSelectedValue)
      ? !!msSelectedValue.length
      : !!msSelectedValue?.value)
  )
})

const resolveSelectOptions = () => {
  console.log(
    `[Select] Resolving ${props.placeholder}, where: ${JSON.stringify(
      props.where,
    )}`,
  )
  selectRef.value?.refreshOptions(null)
}
const clearSelectOptions = () => selectRef.value?.clear()
const selectAll = () => selectRef.value?.selectAll()
//

/////
// For whatever reason multiselect doesn't auto-select existing options after fetch
// fixing it
const shouldSync = () => {
  if (!model.value || (Array.isArray(model.value) && !model.value?.length))
    return false
  const multiselectValue = (selectRef.value?.ev || emptyValue.value) as
    | number
    | number[]
  const multiselectSelected = selectRef.value?.iv
  const multiselectOptions = selectRef.value?.eo || []

  // When bug occurs, MS has value set and options loaded, but it's not visible as selected
  if (mode.value === 'single') {
    return (
      multiselectValue && multiselectOptions.length && !msHasSelectedValue.value
    )
  }
  // With tags, some values can be missing from the selected pool
  if (mode.value === 'tags') {
    const internalValues = multiselectValue as number[]
    const selectedValues = multiselectSelected as Item[]
    return internalValues?.length !== selectedValues.length
  }
}

const hasDesync = computed(() => {
  if (!model.value || (Array.isArray(model.value) && !model.value?.length))
    return false

  const multiselectValue = (selectRef.value?.ev || emptyValue.value) as
    | number
    | number[]
  const multiselectOptions = selectRef.value?.eo || []

  if (mode.value === 'single') {
    return !msHasSelectedValue.value && multiselectValue
  }
  if (mode.value === 'tags') {
    const multiselectValues = (multiselectValue as number[]) || []
    const available = multiselectOptions.filter((o) =>
      multiselectValues.includes(o.value),
    )
    return available.length !== multiselectValues.length
  }
})

function syncValueToSelect() {
  const multiselectOptions = selectRef.value?.eo || []
  const multiselectValue = (selectRef.value?.ev || emptyValue.value) as
    | number
    | number[]

  // console.log(`[Select:syncValueToSelect] ev: ${multiselectValue}`)
  let selected
  if (mode.value === 'single') {
    selected = multiselectOptions.find((o) => o.value === multiselectValue)
  }
  if (mode.value === 'tags') {
    const values = multiselectValue as number[]
    selected = multiselectOptions.filter((o) => values.includes(o.value))
  }
  if (selected) {
    selectRef.value.select(selected)
    return selected
  }
  return null
}

// Should handle the case where there is no entry pre-loaded
// but the value is valid. So we need to re-fetch options with the loaded value
function forceInitOptionsLoad() {
  forceInitialOptionsLoad.value = true
  resolveSelectOptions()
  forceInitialOptionsLoad.value = false
}

watch(hasDesync, () => {
  // Should handle the case where there is no entry pre-loaded
  // but the value is valid. So we need to re-fetch options with the loaded value
  if (
    hasDesync.value &&
    !forceInitialOptionsLoad.value &&
    loadedOptions.value
  ) {
    forceInitOptionsLoad()
    // Wait for new options to load and select if available
    watchOnce(
      () => JSON.stringify(selectRef.value?.eo),
      () => {
        if (shouldSync()) {
          const selected = syncValueToSelect()
          if (!selected) {
            // Consider value in model unreachable, reset it.
            // If code went here by mistake, value probably wasn't available with given ids or where query
            // Might not be the brightest idea with tags because it ignores partial selection of results, but the bug is the bug
            flushValue()
          }
        }
      },
    )
  }
})

////
function getIdsAsArray() {
  if (!model.value) return null

  if (Array.isArray(model.value)) {
    if (!model.value.length) return null

    return model.value
  }

  return [model.value]
}

async function loadInitialOptions(search) {
  const where = props.where || {}
  const values = getIdsAsArray()

  const queries = []
  if (values) {
    queries.push(props.initialOptions(values, where))
  }
  queries.push(props.options(search, where))

  const result = (await Promise.all(queries)).flat()
  loadedOptions.value = true

  // Prevent duplicates
  return result.reduce((acc, v) => {
    if (acc.findIndex((a) => a.value === v.value) < 0) {
      acc.push(v)
    }
    return acc
  }, [])
}

async function loadOptions(search?: string) {
  const where = props.where || {}
  // console.log(
  //   `[Select:o] ${props.placeholder}: ${search}, ${JSON.stringify(props.where)}`,
  // )

  if (props.options instanceof Function) {
    const result = await props.options(search, where)
    loadedOptions.value = true
    return result
  } else {
    const result = props.options
    loadedOptions.value = true
    return result
  }
}

async function options(search: string = null) {
  // console.log(
  //   `[Select:o1] ${props.placeholder}: ${search}, ${JSON.stringify(props.where)}`,
  // )

  if (props.initialOptions) {
    const multiselectOptions = selectRef.value?.eo || []
    let shouldLoad = false
    // We want the ability to force initial options load
    shouldLoad = shouldLoad || forceInitialOptionsLoad.value
    // If we didn't load anything yet but should
    shouldLoad =
      shouldLoad || (props.resolveOnLoad && !multiselectOptions.length)
    // If we already have a set of ids in our model,
    // which could be overwritten by a regular options query
    shouldLoad = shouldLoad || !!getIdsAsArray()

    if (shouldLoad) {
      const opts = await loadInitialOptions(search)
      return opts
    }
  }
  const opts = await loadOptions(search)
  loadedOptions.value = true
  return opts
}

const onOpen = (select$: Multiselect) => {
  if (select$ && (select$ as any).noOptions) {
    select$.resolveOptions(null)
  }
}

const onSearchChange = async (search: string) => {
  const isInternal =
    !(props.options instanceof Function) && !!props.initialOptions
  if (isInternal) {
    const ioptions = (await props.initialOptions()) || []
    return ioptions.filter((option) => {
      if (!search) return true
      const searchStr = search.toLowerCase()
      const label = option.label?.toLowerCase() || ''
      const value = option.value.toString().toLowerCase() || ''
      return label.includes(searchStr) || value.includes(searchStr)
    })
  }
}

const onAddAll = () => {
  // When in single mode, we don't want to add all
  if (props.mode === 'single') return
  // Get all available options with their full structure
  selectAll()
}

const onClear = async (clearSelect: Function, event) => {
  clearSelect(event)
  // if (!loadedOptions.value) await resolveSelectOptions()
  // nextTick(() => flushValue())
}

watch(
  () => JSON.stringify(props.where),
  () => {
    console.info(
      '[Select] WHERE changed to',
      JSON.stringify(props.where),
      'for placeholder:',
      props.placeholder,
    )
    resolveSelectOptions()
  },
)

const isDisabled = computed(() => {
  if (props.disabled) return true
  return !loadedOptions.value && !props.resolveOnLoad
})

defineExpose({
  selectRef,
  clearSelectOptions,
  resolveSelectOptions,
})
</script>
<template>
  <div
    class="flex flex-row align-middle justify-center flex-1"
    :class="{
      'border border-gray-300 rounded-lg outline-none w-full': !props.formkit,
    }"
  >
    <Multiselect
      ref="selectRef"
      v-model="model"
      :options="options"
      :clear-on-select="isSingle"
      :close-on-select="isSingle"
      :loading="loading"
      :filter-results="props.filterResults"
      :infinite="false"
      :groups="false"
      :limit="-1"
      :class="props.size && `size-${props.size}`"
      :resolve-on-load="props.resolveOnLoad"
      :can-deselect="props.canDeselect"
      :placeholder="props.placeholder"
      :searchable="props.searchable"
      :can-clear="props.canClear"
      :disabled="isDisabled"
      :object="props.object"
      :delay="props.delay"
      :caret="props.caret"
      :mode="mode"
      @input="onInput"
      @open="onOpen"
      @search-change="onSearchChange"
      track-by="label"
    >
      <template #caret>
        <div class="multiselect-caret">
          <div class="i-heroicons-solid-selector multiselect-caret-icon" />
        </div>
      </template>
      <template #clear="{ clear }">
        <div class="multiselect-clear">
          <div
            class="i-heroicons-solid-x multiselect-clear-icon"
            @click.stop.prevent="onClear(clear, $event)"
          />
        </div>
      </template>
      <template #spinner>
        <Spinner class="multiselect-spinner" />
      </template>
      <template #singlelabel="{ value }">
        <slot name="singlelabel" :value="value" :select$="selectRef" />
      </template>
      <template #option="{ option }">
        <slot name="option" :option="option">
          <span :class="{ 'text-xs': option.disabled }">{{
            option.label
          }}</span>
          <Badge
            v-if="props.attrs?.activated?.includes(option.value)"
            class="ml-2"
            :check="true"
            :type="'success'"
          />
        </slot>
      </template>
      <template #tag="{ option, handleTagRemove, disabled }">
        <span
          class="multiselect-tag"
          :class="{
            'multiselect-tag': props.mode === 'tags',
            'multiselect-tag-long':
              props.mode === 'tags-long' || props.view === 'longtag',
            'multiselect-tag-full': props.view === 'fulltag',
          }"
        >
          <slot
            name="tag"
            :option="option"
            :handleTagRemove="handleTagRemove"
            :disabled="disabled"
          >
            <span class="truncate">{{ option.label }}</span>
            <span
              v-if="!disabled"
              class="multiselect-tag-remove group"
              @click.prevent="handleTagRemove(option, $event)"
            >
              <div class="i-heroicons-solid-x multiselect-tag-remove-icon" />
            </span>
          </slot>
        </span>
      </template>
    </Multiselect>
    <Button
      @click="onAddAll()"
      class="fill-all inline-block text-xs !p-0 !px-2 my-1"
      variant="simple"
      v-if="props.showAddAll"
      >Fill All</Button
    >
  </div>
</template>
<style lang="postcss">
.is-active .fill-all {
  border: none!;
}
.multiselect-wrapper {
  @apply relative mx-auto w-full flex items-center justify-end box-border cursor-pointer outline-none py-2;
}
.multiselect-assistive-text {
  @apply absolute -m-px w-px h-px overflow-hidden;
  clip: rect(0 0 0 0);
}
.multiselect {
  @apply relative flex-grow flex items-center justify-end box-border cursor-pointer text-gray-800 rounded-lg bg-white text-sm leading-snug outline-none;
}
.has-error .multiselect {
  @apply border-red-600;
}
.multiselect.size-small {
  @apply text-xs;
}
.multiselect.is-disabled {
  @apply cursor-default bg-gray-100;
}
/* .multiselect.is-active {
  @apply ring-1 ring-primary-500 border-primary-500 outline-none;
} */
.multiselect-multiple-label,
.multiselect-single-label {
  @apply flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug px-3.5 text-sm;
}
.multiselect-single-label {
  @apply w-full;
  .multiselect-single-label-text {
    @apply overflow-hidden text-ellipsis whitespace-nowrap pr-9;
  }
}
.multiselect-search {
  @apply w-full absolute inset-0 outline-none appearance-none border-none focus:outline-none focus:ring-0 focus:z-10 box-border text-sm font-sans bg-white rounded-lg pl-3.5 !important;
}
.multiselect-tags {
  @apply flex-grow flex-shrink flex flex-wrap items-center mt-1 pl-2;
}
.multiselect-tag {
  @apply bg-gray-100 text-gray-700 max-w-[15rem] text-xs font-semibold py-0.5 pl-2 rounded mr-1 mb-1 flex items-center whitespace-nowrap;
}
.multiselect-tag-full {
  @apply bg-gray-100 text-gray-700 !max-w-full text-xs font-semibold py-0.5 pl-2 rounded mr-1 mb-1 flex items-center whitespace-nowrap;
}
.multiselect-tag-long {
  @apply bg-gray-100 text-gray-700 !max-w-[40rem] text-xs font-semibold py-0.5 pl-2 rounded mr-1 mb-1 flex items-center whitespace-nowrap;
}
.multiselect-tag.is-disabled {
  @apply pr-2 opacity-50;
}
.multiselect-tag-remove {
  @apply flex items-center justify-center p-1 mx-0.5 rounded-sm hover:bg-black hover:bg-opacity-5;
}
.multiselect-tag-remove-icon {
  @apply opacity-30 inline-block w-3 h-3 group-hover:opacity-70;
}
.multiselect-tags-search-wrapper {
  @apply inline-block relative mx-1 mb-1 flex-grow flex-shrink h-full;
}
.multiselect-tags-search {
  @apply absolute inset-0 border-0 outline-none appearance-none focus:outline-none focus:ring-0 p-0 text-sm font-sans box-border w-full !important;
}
.multiselect-tags-search-copy {
  @apply invisible whitespace-pre-wrap inline-block h-px;
}
.multiselect-placeholder {
  @apply flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 pr-5 text-sm text-gray-400;
}
.multiselect-caret {
  @apply flex items-center bg-center bg-no-repeat h-4 py-px box-content mr-3.5 relative opacity-40 flex-shrink-0 flex-grow-0 transition-transform transform pointer-events-none;
}
.multiselect-caret.is-open {
  @apply rotate-180 pointer-events-auto;
}
.multiselect-clear {
  @apply pr-2 relative opacity-40 transition duration-300 flex-shrink-0 flex-grow-0 flex hover:opacity-80;
}
.multiselect-clear-icon {
  @apply w-4 h-4 py-px box-content inline-block;
}
.multiselect-spinner {
  @apply w-4 h-4 mr-3.5 flex-shrink-0 flex-grow-0 text-gray-600;
}
.multiselect-dropdown {
  @apply max-h-60 absolute -left-px -right-px -bottom-1 transform translate-y-full border border-gray-200 overflow-y-auto z-30 py-1 mt-1 bg-white flex flex-col rounded-lg shadow-lg;
}
.multiselect-dropdown.is-top {
  @apply -translate-y-full top-px bottom-auto flex-col-reverse;
}
.multiselect-dropdown.is-hidden {
  @apply hidden;
}
.multiselect-options {
  @apply flex flex-col p-0 m-0 list-none;
}
.multiselect-options.is-top {
  @apply flex-col-reverse;
}
.multiselect-group {
  @apply relative p-0 m-0;
}
.multiselect-group-label {
  @apply flex sticky -top-1 text-sm box-border items-center justify-start text-left uppercase text-xs text-gray-400 py-1.5 px-3 font-semibold bg-gray-50 cursor-default leading-normal z-10 !important;
}
.multiselect-group-label.is-pointable {
  @apply cursor-pointer;
}
.multiselect-group-label.is-pointed {
  @apply bg-gray-300 text-gray-700;
}
.multiselect-group-label.is-selected {
  @apply bg-green-600 text-white;
}
.multiselect-group-label.is-disabled {
  @apply bg-gray-100 text-gray-300 cursor-not-allowed;
}
.multiselect-group-label.is-selected.is-pointed {
  @apply bg-green-600 text-white opacity-90;
}
.multiselect-group-label.is-selected.is-disabled {
  @apply bg-opacity-50 cursor-not-allowed;
}
.multiselect-group-options {
  @apply p-0 m-0;
}
.multiselect-option {
  @apply flex items-center justify-start box-border text-left cursor-pointer text-sm leading-snug py-2 px-3;
}
.multiselect-option.is-pointed {
  @apply text-gray-800 bg-gray-100;
}
.multiselect-option.is-selected {
  @apply text-gray-800 bg-white font-semibold;
}
.multiselect-option.is-disabled {
  @apply text-gray-400 cursor-not-allowed hover:text-gray-700 !important;
}
.multiselect-option.is-selected.is-pointed {
  @apply text-gray-800 bg-gray-100;
}
.multiselect-option.is-selected.is-disabled {
  @apply bg-opacity-50 cursor-not-allowed;
}
.multiselect-no-options,
.multiselect-no-results {
  @apply py-2 px-3 text-gray-600 bg-white;
}
.multiselect-fake-input {
  @apply bg-transparent absolute left-0 right-0 -bottom-px w-full h-px border-0 p-0 appearance-none outline-none text-transparent;
}
.multiselect-spacer {
  @apply h-[2.375rem] py-px box-content;
}
.multiselect.size-small .multiselect-spacer {
  @apply h-[2.125rem];
}
</style>
